import { WhatsAppMediaType } from '../../../../utils/whatsapp/media.util';
import { useWhatsAppMediaUploadMutation } from '../../../../../shared/hooks/api/messaging/whatsapp/media-upload.mutation';
import { UploadStep } from '../../../../types/whatsapp/media.type';
import { useFileUploadState } from './file-upload-state';
import { mapMediaTypeToMessageType } from '../../../../utils/whatsapp/media-mapping.util';

interface UseWhatsAppMediaUploadParams {
  fromPhoneNumber?: string;
  onUploadError?: (error: string) => void;
}

interface UseWhatsAppMediaUploadReturn {
  // State
  selectedFile: File | null;
  selectedMediaType: WhatsAppMediaType | null;
  uploadedFileId: string | null;
  uploadStep: UploadStep;
  uploadError: string | null;
  isUploading: boolean;

  // Actions
  handleFileSelect: (file: File, mediaType: WhatsAppMediaType) => Promise<void>;
  handleFileValidationError: (error: string) => void;
  handleRemoveFile: () => void;
  mapMediaTypeToMessageType: typeof mapMediaTypeToMessageType;
}

/**
 * Main hook for WhatsApp media upload functionality.
 * It will handle the file upload process, including validation, upload, and error handling.
 * It will also provide a function to map the media type to the message type.
 * It will also provide a function to remove the file.
 */
export const useWhatsAppMediaUpload = ({
  fromPhoneNumber,
  onUploadError,
}: UseWhatsAppMediaUploadParams): UseWhatsAppMediaUploadReturn => {
  const {
    selectedFile,
    selectedMediaType,
    uploadedFileId,
    uploadStep,
    uploadError,
    setSelectedFile,
    setSelectedMediaType,
    setUploadedFileId,
    setUploadStep,
    setUploadError,
    resetFileState,
  } = useFileUploadState();

  const { uploadFile, loading: isUploading } = useWhatsAppMediaUploadMutation(
    (data) => {
      setUploadedFileId(data.fileId ?? null);
      setUploadStep('success');
      setUploadError(null);
    },
    (error) => {
      setUploadError(error.message);
      setUploadStep('error');
      setUploadedFileId(null);
      onUploadError?.(error.message);
    }
  );

  const handleFileSelect = async (file: File, mediaType: WhatsAppMediaType) => {
    if (!fromPhoneNumber) {
      const error = 'No phone number available for upload';
      setUploadError(error);
      setUploadStep('error');
      onUploadError?.(error);
      return;
    }

    setSelectedFile(file);
    setSelectedMediaType(mediaType);
    setUploadStep('uploading');
    setUploadError(null);

    try {
      await uploadFile({
        file,
        fromPhoneNumber,
      });
    } catch (error) {
      console.error(error);
    }
  };

  const handleFileValidationError = (error: string) => {
    setUploadError(error);
    setUploadStep('error');
    onUploadError?.(error);
  };

  const handleRemoveFile = () => {
    resetFileState();
  };

  return {
    // State
    selectedFile,
    selectedMediaType,
    uploadedFileId,
    uploadStep,
    uploadError,
    isUploading,

    // Actions
    handleFileSelect,
    handleFileValidationError,
    handleRemoveFile,
    mapMediaTypeToMessageType,
  };
};
